/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Fields } from "@cd2/forms"
import { IconButton, Placeholder, Spinner } from "@cd2/components"
import { PopoutInput } from "./PopoutInput"
import { doo, IOptionalPromise } from "@cd2/tools/build/es6"
import { P } from "@cd2/text/build/es6"
import { useTheme } from "@cd2/theme/build/es6"
import { createStoreBuilder, useStoreState } from "../utils/stores"
import { messageFromError } from "../utils/error"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { formPadding } from "../utils/styleUtils"
import { icons } from "../utils/icons"
import { Frame } from "./Frame"
import { DelayedRender } from "./DelayedRender"
import classNames from "classnames"

interface INewAsyncSelectState {
  options: any[] | null
  hasMore: boolean
  onceHadOptions: boolean
  optionsLoadError: string | null
  resultQuery: string
  query: string
  isOpen: boolean
}

type INewAsyncSelectActions =
  | { type: "clearOptions" }
  | { type: "optionsLoaded"; options: any[]; query: string; hasMore: boolean }
  | { type: "optionsLoadError"; error: unknown; query: string }
  | { type: "queryChanged"; query: string }
  | { type: "open" }
  | { type: "close" }

const buildNewAsyncSelectStore = createStoreBuilder<INewAsyncSelectState, INewAsyncSelectActions>({
  optionsLoaded(state, { options, hasMore, query }) {
    state.options = options
    state.hasMore = hasMore
    state.resultQuery = query
    state.onceHadOptions = true
    state.optionsLoadError = null
  },
  optionsLoadError(state, { error, query }) {
    state.options = []
    state.resultQuery = query
    state.optionsLoadError = messageFromError(error)
  },
  clearOptions(state) {
    state.options = null
  },
  queryChanged(state, { query }) {
    state.query = query
  },
  open(state) {
    state.isOpen = true
  },
  close(state) {
    state.isOpen = false
  },
})

type INewAsyncSelectFieldProps<T> = {
  keyExtractor(item: T): string
  value: T | null | undefined | "loading" | "error"
  loadOptions(filter: {
    query: string
  }): IOptionalPromise<T[] | { options: T[]; hasMore?: boolean }>
  renderOption(item: T): React.ReactNode
  label?: React.ReactNode
  showRequiredStar?: boolean
  error?: string | boolean
  disabled?: boolean
  autoFocus?: boolean
  placeholder?: string
  reloadOptionsKey?: string
  noResultsMessage?: string
  dontLoadUntilOpen?: boolean
  optionPadding?: number
} & (
  | {
      isClearable?: false
      onChange(item: T): void
    }
  | {
      isClearable: true
      onChange(item: T | null): void
    }
)

export function NewAsyncSelectField<T>({
  value,
  loadOptions,
  renderOption,
  onChange,
  label,
  showRequiredStar,
  keyExtractor,
  error,
  disabled,
  isClearable,
  placeholder = "Select...",
  reloadOptionsKey,
  noResultsMessage,
  dontLoadUntilOpen,
  optionPadding,
}: INewAsyncSelectFieldProps<T>) {
  const theme = useTheme()

  const store = useMemo(
    () =>
      buildNewAsyncSelectStore({
        query: "",
        options: null,
        hasMore: false,
        optionsLoadError: null,
        onceHadOptions: false,
        resultQuery: "",
        isOpen: false,
      }),
    [],
  )
  const state = useStoreState(store)
  const popoutRef = useRef<PopoutInput.IContext>()
  const debounceRef = useRef<any>()

  const queryInputRef = useRef<HTMLInputElement>(null)

  useAsyncEffect(
    ({ isAlive }) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      if (dontLoadUntilOpen && store.getState().isOpen === false) {
        console.log("Skipping load as not open")
        return
      }
      const query = state.query

      debounceRef.current = setTimeout(async () => {
        const clearOptionsTimeout = setTimeout(() => {
          store.dispatch({ type: "clearOptions" })
        }, 500)

        try {
          const result = await loadOptions({ query })

          clearTimeout(clearOptionsTimeout)

          if (!isAlive()) {
            return
          }
          let options: T[] = []
          let hasMore = false
          if (Array.isArray(result)) {
            options = result
          } else {
            options = result.options
            hasMore = result.hasMore ?? false
          }

          store.dispatch({ type: "optionsLoaded", options, query, hasMore })
        } catch (error) {
          console.error("Error in loadOptions for NewAsyncSelect")
          console.error(error)

          clearTimeout(clearOptionsTimeout)

          if (!isAlive()) {
            return
          }

          store.dispatch({ type: "optionsLoadError", query, error })
        }
      }, 300)
    },
    [state.query, reloadOptionsKey],
  )

  const [focusIndex, setFocusIndex] = useState<number | null>(null)
  useEffect(() => {
    const el = document.querySelector(`[data-focus-index="${focusIndex}"]`)

    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
    }
  }, [focusIndex])

  useEffect(() => {
    if (focusIndex !== null) {
      setFocusIndex(null)
    }

    const raf = requestAnimationFrame(() => {
      if (state.isOpen && value && value !== "loading" && value !== "error") {
        const selectedOption: HTMLElement | null = document.querySelector(
          `[data-option-key="${typeof value === "string" ? value : keyExtractor(value)}"]`,
        )

        if (selectedOption) {
          // This first call acts on ALL scrollable containers, including the
          // body element. This means that if the popout is slightly off screen,
          // we will scroll the page to view it.
          selectedOption.scrollIntoView({ block: "nearest", inline: "nearest" })

          // This next check is specifically doing the popout scroll container.
          // We do this separately because we want the element positioned at the
          // top of the list.
          if (
            selectedOption.parentElement &&
            selectedOption.parentElement.scrollHeight > selectedOption.parentElement.clientHeight
          ) {
            selectedOption.parentElement.scrollTop =
              selectedOption.offsetTop - selectedOption.parentElement.offsetTop
          }
        }
      }
    })

    return () => cancelAnimationFrame(raf)
  }, [state.isOpen, state.options])

  function handleSelect(opt: T) {
    onChange(opt)
    popoutRef.current?.popover.hide()
  }

  async function handleOnShown() {
    console.log("handleOnShown")
    store.dispatch({ type: "open" })
    queryInputRef.current?.select()

    const shouldLoad = state.options === null || state.resultQuery !== state.query
    if (shouldLoad) {
      try {
        const result = await loadOptions({ query: state.query })
        let options: T[] = []
        let hasMore = false
        if (Array.isArray(result)) {
          options = result
        } else {
          options = result.options
          hasMore = result.hasMore ?? false
        }

        store.dispatch({ type: "optionsLoaded", options, hasMore, query: state.query })
      } catch (error) {
        console.error("Error in loadOptions for NewAsyncSelect")
        console.error(error)
        store.dispatch({ type: "optionsLoadError", query: state.query, error })
      }
    }
  }

  function handleOnHidden() {
    store.dispatch({ type: "close" })
  }

  const showLoadingSpinner = state.query !== state.resultQuery

  const mainContent = doo(() => {
    if (value === null || value === undefined) {
      return (
        <span
          css={css`
            color: ${theme.forms.placeholderColor};
            font-style: italic;
          `}
        >
          {placeholder}
        </span>
      )
    } else if (value === "loading") {
      return <Placeholder />
    } else if (value === "error") {
      return (
        <P color="red" size="small">
          An error occurred loading this resource
        </P>
      )
    } else {
      return <div>{renderOption(value)}</div>
    }
  })

  function handleFocusPrevious() {
    if (!state.isOpen || !state.options) {
      return
    }

    let newFocus = null
    if (focusIndex === null) {
      newFocus = state.options.length - 1
    } else if (focusIndex === 0) {
      newFocus = state.options.length - 1
    } else {
      newFocus = focusIndex - 1
    }

    setFocusIndex(newFocus)
  }
  function handleFocusNext() {
    if (!state.isOpen || !state.options) {
      return
    }

    if (focusIndex === null) {
      setFocusIndex(0)
    } else if (focusIndex === state.options.length - 1) {
      setFocusIndex(0)
    } else {
      setFocusIndex(focusIndex + 1)
    }
  }
  function handleSelectFocussed() {
    if (!state.isOpen || !state.options || focusIndex === null) {
      return
    }

    handleSelect(state.options[focusIndex])
  }

  return (
    <PopoutInput
      label={label}
      showRequiredStar={showRequiredStar}
      error={error}
      disabled={disabled}
      onShown={handleOnShown}
      onHidden={handleOnHidden}
      extendTheme={{
        main: { padding: 0 },
      }}
      renderMain={() => (
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <div
            css={css`
              padding: ${optionPadding && value !== null && value !== undefined
                ? `${optionPadding}px`
                : formPadding(theme.forms.padding ?? { topAndBottom: 10, sides: 9 })};
              overflow: hidden;
              text-overflow: ellipsis;
              flex: 1;
            `}
          >
            {mainContent}
          </div>
          {isClearable && value !== null && value !== undefined && (
            <div
              // We have to use onClickCapture here to prevent the popout from receiving the click event
              // as it manually is adding an event listener to the element instead of using React's onClick
              onClickCapture={e => {
                e.preventDefault()
                e.stopPropagation()
                // @ts-ignore this is only rendered if isClearable is true
                onChange(null)
              }}
            >
              <IconButton buttonStyle="text" color="#333333" padding={8} icon={icons.x} square />
            </div>
          )}
        </div>
      )}
      renderOptions={ctx => {
        popoutRef.current = ctx
        return (
          <div
            css={css`
              width: 100%;
            `}
          >
            <div
              css={css`
                border-bottom: 1px solid #ccc;
                position: relative;
              `}
            >
              <Fields.Text
                ref={queryInputRef}
                value={state.query}
                onChange={value => store.dispatch({ type: "queryChanged", query: value })}
                placeholder="Search..."
                inputProps={{
                  autoFocus: true,
                  type: "search",
                  onFocus(e) {
                    e.target.select()
                  },
                  onKeyDown(e) {
                    if (e.key === "ArrowUp") {
                      e.preventDefault()
                      handleFocusPrevious()
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault()
                      handleFocusNext()
                    } else if (e.key === "Enter") {
                      e.preventDefault()
                      handleSelectFocussed()
                    }
                  },
                }}
                css={css`
                  border: none !important;

                  input {
                    border: none !important;
                  }
                `}
              />
              {showLoadingSpinner && (
                <div
                  css={css`
                    position: absolute;
                    right: 30px;
                    top: 50%;
                    transform: translate(0, -50%);
                  `}
                >
                  <Spinner color="black" size={24} />
                </div>
              )}
            </div>
            {state.optionsLoadError ? (
              <Frame
                minHeight={40}
                css={css`
                  padding: 16px;
                  background: #ffe6e6;
                `}
              >
                <div>
                  <P color="red" weight={600}>
                    An error occurred loading this resource
                  </P>
                  <P
                    color="red"
                    size={"small"}
                    css={css`
                      opacity: 0.8;
                      font-family: monospace;
                    `}
                  >
                    {state.optionsLoadError}
                  </P>
                </div>
              </Frame>
            ) : (
              <React.Fragment>
                {!state.onceHadOptions && (
                  <Frame minHeight={100}>
                    <DelayedRender>
                      <Spinner color="black" />
                    </DelayedRender>
                  </Frame>
                )}
                {state.options && state.options.length === 0 && (
                  <Frame
                    minHeight={40}
                    css={css`
                      color: grey;
                      padding: 16px;
                    `}
                  >
                    {noResultsMessage || "No results"}
                  </Frame>
                )}
                {state.options && state.options.length > 0 && (
                  <div
                    css={css`
                      max-height: 300px;
                      overflow-y: auto;
                    `}
                  >
                    {state.options.map((opt, i) => (
                      <div
                        key={keyExtractor(opt)}
                        css={css`
                          padding: 8px 12px;
                          transition: 0.1s;
                          cursor: pointer;

                          :hover {
                            background: #efefef;
                          }

                          &.focused {
                            background: #efefef;
                          }

                          &.selected {
                            background: #e6f5ff;
                          }
                        `}
                        onClick={() => handleSelect(opt)}
                        className={classNames({
                          focused: focusIndex === i,
                          selected:
                            value &&
                            value !== "loading" &&
                            value !== "error" &&
                            keyExtractor(value) === keyExtractor(opt),
                        })}
                        data-focus-index={i}
                        data-option-key={keyExtractor(opt)}
                      >
                        {renderOption(opt)}
                      </div>
                    ))}
                  </div>
                )}
                {state.hasMore && (
                  <div
                    css={css`
                      padding: 8px 12px;
                      background: #f5f5f5;
                      text-align: center;
                    `}
                  >
                    Showing first {state.options?.length} results. Search to filter more.
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        )
      }}
    />
  )
}
